// PART 1: Imports and Initial Setup
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API_BASE_URL, SOCKET_URL } from "../config";
import { Link } from "react-router-dom";
import io from "socket.io-client";

const MyBids = () => {
  useAuth(); // Auth context wird benötigt, aber user wird nicht verwendet
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    fetchMyBids();
    const socket = io(SOCKET_URL);
    socket.on("connect", () => {});
    socket.on("newBid", () => fetchMyBids());
    socket.on("bidUpdate", () => fetchMyBids());
    socket.on("auctionUpdate", () => fetchMyBids());
    return () => socket.disconnect();
  }, []);

  // PART 2: Helper Functions and Data Fetching
  const fetchMyBids = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/bids/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.bids) {
        const sortedBids = response.data.bids.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setBids(sortedBids);
      } else {
        setError("Unerwartetes Datenformat von der API");
      }
    } catch (error) {
      console.error("Error details:", error.response || error);
      setError(
        "Fehler beim Laden Ihrer Gebote: " +
          (error.response?.data?.message ||
            "Ein unerwarteter Fehler ist aufgetreten")
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groupBidsByTrikot = (bids) => {
    return bids.reduce((groups, bid) => {
      const trikotId = bid.trikotId._id;
      if (!groups[trikotId]) {
        groups[trikotId] = {
          trikotInfo: {
            id: trikotId,
            number: bid.trikotId.number,
            playerName: bid.trikotId.playerName,
            currentBid: bid.trikotId.currentBid,
            endTime: bid.trikotId.endTime,
          },
          bids: [],
        };
      }
      groups[trikotId].bids.push(bid);
      // Sort bids by date in descending order (newest first)
      groups[trikotId].bids.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      return groups;
    }, {});
  };

  const toggleGroup = (trikotId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [trikotId]: !prev[trikotId],
    }));
  };

  const renderBidStatus = (group, isMobile = false) => {
    const isHighestBidder =
      group.bids[0].amount === group.trikotInfo.currentBid;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          width: isMobile ? "100%" : "auto",
        }}
      >
        <Chip
          label={isHighestBidder ? "Höchstbietender" : "Überboten"}
          color={isHighestBidder ? "success" : "error"}
          variant="outlined"
        />
        {!isHighestBidder &&
          new Date(group.trikotInfo.endTime) > new Date() && (
            <Link
              to={`/trikot/${group.trikotInfo.id}`}
              style={{
                textDecoration: "none",
                color: "#1976d2",
                fontSize: "0.875rem",
              }}
            >
              Erneut bieten
            </Link>
          )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // PART 3: Render Logic and Return Statement
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Meine Gebote
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {bids.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography>Sie haben noch keine Gebote abgegeben.</Typography>
        </Paper>
      ) : isMobile ? (
        // Mobile view with grouped cards
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.values(groupBidsByTrikot(bids)).map((group) => (
            <Card key={group.trikotInfo.id}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    Trikot #{group.trikotInfo.number} -{" "}
                    {group.trikotInfo.playerName}
                  </Typography>
                  <IconButton
                    onClick={() => toggleGroup(group.trikotInfo.id)}
                    size="small"
                  >
                    {expandedGroups[group.trikotInfo.id] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    Aktuelles Höchstgebot:
                  </Typography>
                  <Typography fontWeight="bold">
                    {group.trikotInfo.currentBid.toFixed(2)}€
                  </Typography>
                </Box>

                {renderBidStatus(group, true)}

                <Collapse in={expandedGroups[group.trikotInfo.id]}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Mein Gebotsverlauf:
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {group.bids.map((bid) => (
                      <Box
                        key={bid._id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(bid.createdAt)}
                        </Typography>
                        <Typography variant="body2">
                          {bid.amount.toFixed(2)}€
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Auktion endet:{" "}
                    {new Date(group.trikotInfo.endTime) > new Date()
                      ? formatDateTime(group.trikotInfo.endTime)
                      : "Beendet"}
                  </Typography>
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop view with grouped table rows
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Trikot</TableCell>
                <TableCell>Spieler</TableCell>
                <TableCell align="right">Gebote</TableCell>
                <TableCell align="right">Aktuelles Höchstgebot</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Auktion endet</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(groupBidsByTrikot(bids)).map((group) => (
                <React.Fragment key={group.trikotInfo.id}>
                  <TableRow>
                    <TableCell>#{group.trikotInfo.number}</TableCell>
                    <TableCell>{group.trikotInfo.playerName}</TableCell>
                    <TableCell align="right">{group.bids.length}</TableCell>
                    <TableCell align="right">
                      {group.trikotInfo.currentBid.toFixed(2)}€
                    </TableCell>
                    <TableCell align="center">
                      {renderBidStatus(group)}
                    </TableCell>
                    <TableCell align="right">
                      {new Date(group.trikotInfo.endTime) > new Date() ? (
                        formatDateTime(group.trikotInfo.endTime)
                      ) : (
                        <Chip label="Beendet" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => toggleGroup(group.trikotInfo.id)}
                        size="small"
                      >
                        {expandedGroups[group.trikotInfo.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                    >
                      <Collapse
                        in={expandedGroups[group.trikotInfo.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Mein Gebotsverlauf:
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {group.bids.map((bid) => (
                                <TableRow key={bid._id}>
                                  <TableCell>
                                    {formatDateTime(bid.createdAt)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {bid.amount.toFixed(2)}€
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default MyBids;
